import { Button, Flex } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AppContext, { CurrentUser } from 'src/AppContext';
import styled from 'styled-components';
import FormElement from '../FormElement';

export type UserData = Pick<CurrentUser, 'firstName' | 'lastName' | 'email' | 'userId' | 'role' | 'orgName'>;
export type FormModel = Required<Omit<UserData, 'userId'>>;

export interface EditUserNameModalProps {
    onClose?: () => void;
    onFormSubmit: (formData: FormModel) => void;
};

const EditUserNameModal: FunctionComponent<EditUserNameModalProps> = (props) => {
    const { t } = useTranslation();
    const { register, handleSubmit, formState: { errors } } = useForm<FormModel>({ mode: 'onBlur' });
    const user = useContext(AppContext).user!;

    const registerOptionsForName =
    {
        required: t('Validation.forms.field_required_val', 'Please fill out this field.'),
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        setValueAs: (value: string) => value.trim(),
    };

    const getDefaultValueForProp = (propName: keyof FormModel) => {
        if (!user[propName]) return;
        return { defaultValue: user[propName] };
    };

    return (
        <Flex flexDirection='column' gap='16px' width='100%' aria-label='Update username modal'>
            <form onSubmit={handleSubmit(formdata => { props.onFormSubmit(formdata); })}>
                <Flex>
                    <FormElement
                        dataProperty="firstName"
                        registerElement={{ ...register('firstName', registerOptionsForName) }}
                        validationError={errors?.firstName?.message}
                        label={t('UpdateUserNameModalContainer.first_name_title', 'First name')}
                        placeholder={t('UpdateUserNameModalContainer.input_firstname_desc', 'Enter first name')}
                        {...getDefaultValueForProp('firstName')}
                    />
                    <FormElement
                        dataProperty="lastName"
                        registerElement={{ ...register('lastName', registerOptionsForName) }}
                        validationError={errors?.lastName?.message}
                        label={t('UpdateUserNameModalContainer.family_name_title', 'Last name')}
                        placeholder={t('UpdateUserNameModalContainer.input_lastname_desc', 'Enter last name')}
                        {...getDefaultValueForProp('lastName')}
                    />
                </Flex>
                <FormElement
                    dataProperty="email"
                    registerElement={{ ...register('email') }}
                    label={'Email adress'}
                    placeholder={t('NewOrgModal.input_name_desc', 'Enter email adress')}
                    {...getDefaultValueForProp('email')}
                    readonly
                />

                <OrganizationInfoSection>
                    <StyledFlex>
                        <Box className="px10_600_normal">{t('UserManagement.tableLabels.organization_name_title', 'Name')}
                        </Box>
                        <div className="px10_600_normal">{t('AddOrEditUserModal.role_title', 'User role')}
                        </div>
                    </StyledFlex>
                    <Flex flexDirection='row' pl='24px' mt='5px'>
                        <Box className="px12_400_normal">
                            {user.orgName}
                        </Box>
                        <div className="px12_400_normal">
                            {t(`Roles.${user.role}_label`, 'Unknown role')}
                        </div>
                    </Flex>
                </OrganizationInfoSection>

                <ButtonContainer>
                    <Button
                        variant="primary"
                        type="submit"
                    >
                        {t('ButtonTitles.action_apply_btn', 'Apply')}
                    </Button>
                    <Button
                        variant="secondary"
                        type="reset"
                        onClick={props.onClose}
                    >
                        {t('NewOrgModal.action_cancel_btn', 'Cancel')}
                    </Button>
                </ButtonContainer>

            </form>
        </Flex>
    );
};

const ButtonContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding-right: 23px;
    & button {
        margin-left: 10px;
    }
`;

const OrganizationInfoSection = styled.div`
    width: 100%;
    margin: 8px 0 16px;
    padding: 12px 0;
    gap: 170px;
`;

const StyledFlex = styled.div`
    display: flex;
    flex-direction: row;
    padding-left: 24px;
    padding-bottom: 5px;
    border-bottom: 2px solid ${({ theme }) => theme.colors.backgroundPaper};
  `;

const Box = styled.div` //so stuff stays aligned independent of orgname length
    width: 282px;
  `;

export default EditUserNameModal;
